import { useEffect, useState } from 'react';
import { LoadingSpinner } from '@clay-run/glaze/loading-spinner';
import { IconToggleButton } from '@clay-run/glaze/toggle-button';
import { useTrellusLauncherHidden } from '../../../lib/trellus/launcher-hide';
import { hasTrellusCommand, useTrellusHostStore } from '../../../lib/trellus/trellus-host-store';
import {
  toggleTrellusSidebar,
  useTrellusSidebarStore,
  type TrellusSidebarActionResult,
} from '../../../lib/trellus/trellus-sidebar-store';
import { FLOW_ICONS } from '../icons';

/**
 * Headset button — opens/closes the Trellus dialer sidebar. Rendered by
 * `PanelToggles` right of the Actions toggle, so it travels with the toggle
 * cluster to whichever host (Header / RailDock) paints it.
 *
 * Renders NOTHING (no placeholder, no layout shift) unless the Trellus handshake succeeded and `sidebar.toggle` is
 * advertised. No Terra Beta gate: every rep with the extension sees it.
 *
 * `aria-pressed` mirrors the sidebar state Trellus last REPORTED (null =
 * unknown = not pressed); a click only sends `sidebar.toggle` and the pressed
 * state follows the result/push — never optimistic. v15 provides no queue
 * count or call-active flag, so there is deliberately no count badge or
 * live-call dot here; only a Gong-disconnected warning dot.
 *
 * While visible it also hides Trellus's floating launcher pill
 * (`useTrellusLauncherHidden`), and gives it back when it unmounts.
 */

const MESSAGE_MS = 4000;

export const HEADSET_MESSAGES = {
  busy: 'Finish or save the current call first',
  unavailable: "Dialer isn't available on this page",
  failed: "Couldn't update the dialer. Try again.",
} as const;

export function headsetMessageFor(result: TrellusSidebarActionResult): string | null {
  switch (result.status) {
    case 'applied': return null;
    case 'busy': return null; // our own command is still in flight; the busy spinner already says so
    case 'rejected':
      if (result.reason_code === 'sidebar_busy') return HEADSET_MESSAGES.busy;
      if (result.reason_code === 'view_unavailable') return HEADSET_MESSAGES.unavailable;
      return HEADSET_MESSAGES.failed;
    case 'unsupported':
    case 'failed':
      return HEADSET_MESSAGES.failed;
  }
}

export function HeadsetButton() {
  const supported = useTrellusHostStore(s => s.extension_present && hasTrellusCommand(s, 'sidebar.toggle'));
  if (!supported) return null;
  return <HeadsetControl />;
}

function HeadsetControl() {
  const open = useTrellusSidebarStore(s => s.open);
  const busy = useTrellusSidebarStore(s => s.busy);
  const gongDisconnected = useTrellusSidebarStore(s => s.gong_state === 'disconnected');
  const [message, setMessage] = useState<{ id: number; text: string } | null>(null);

  useTrellusLauncherHidden(true);

  const messageId = message?.id;
  useEffect(() => {
    if (messageId === undefined) return;
    const timer = setTimeout(() => setMessage(null), MESSAGE_MS);
    return () => clearTimeout(timer);
  }, [messageId]);

  const pressed = open === true;
  // Glaze's IconToggleButton forwards only a fixed prop set (no aria-busy / aria-describedby), so the
  // Gong warning rides on the accessible name and busy rides on a spinner overlay.
  const label = `${pressed ? 'Close dialer' : 'Open dialer'}${gongDisconnected ? ', Gong is not connected' : ''}`;

  const onToggle = (): void => {
    void toggleTrellusSidebar().then(result => {
      const text = headsetMessageFor(result);
      setMessage(text === null ? null : { id: Date.now() + Math.random(), text });
    });
  };

  return (
    <span className="relative flex shrink-0 items-center" data-testid="headset-button">
      <IconToggleButton
        size="sm"
        variant="ghost"
        className={`shrink-0 data-pressed:bg-bg-selected${busy ? ' [&_svg]:opacity-30' : ''}`}
        icon={FLOW_ICONS.dialer}
        aria-label={label}
        pressed={pressed}
        onPressedChange={onToggle}
      />
      {busy && (
        <span aria-hidden="true" data-testid="headset-busy" className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <LoadingSpinner size="sm" />
        </span>
      )}
      {gongDisconnected && (
        <span aria-hidden="true" data-testid="headset-gong-dot" className="pointer-events-none absolute right-0.5 top-0.5 size-2 rounded-full bg-icon-warning" />
      )}
      <span role="status" aria-live="polite" className={message ? 'absolute right-0 top-full z-50 mt-1 w-max max-w-64 rounded-md border border-border-primary bg-bg-primary px-2 py-1 text-sm text-content-primary shadow-md' : 'sr-only'}>
        {message?.text ?? ''}
      </span>
    </span>
  );
}
